import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { MapPin, User, Calendar, ChevronLeft, ChevronRight, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import Footer from "../components/landing/Footer";
import Navigation from "../components/landing/Navigation";
import SEO from "../components/seo/SEO";
import { api, BASE } from "../services/api";

function resolveImg(url) {
    if (!url) return "";
    return url.startsWith("http") ? url : `${BASE}${url}`;
}

function fmtDate(iso) {
    if (!iso) return "";
    try { return new Date(iso).toLocaleDateString("sk-SK", { day: "numeric", month: "long", year: "numeric" }); }
    catch { return iso; }
}

function parseVideoEmbed(url) {
    if (!url) return null;
    const yt = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/);
    if (yt) return `https://www.youtube.com/embed/${yt[1]}`;
    const vm = url.match(/vimeo\.com\/(\d+)/);
    if (vm) return `https://player.vimeo.com/video/${vm[1]}`;
    return null;
}

function Lightbox({ images, startIdx, onClose }) {
    const [idx, setIdx] = useState(startIdx);

    useEffect(() => {
        const handler = (e) => {
            if (e.key === "Escape") onClose();
            if (e.key === "ArrowLeft")  setIdx(i => Math.max(0, i - 1));
            if (e.key === "ArrowRight") setIdx(i => Math.min(images.length - 1, i + 1));
        };
        window.addEventListener("keydown", handler);
        document.body.style.overflow = "hidden";
        return () => { window.removeEventListener("keydown", handler); document.body.style.overflow = ""; };
    }, [images.length, onClose]);

    return (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/97 backdrop-blur-md flex items-center justify-center">
            <button onClick={onClose}
                className="absolute top-4 right-4 p-2 text-zinc-400 hover:text-white transition-colors z-10">
                <X className="w-6 h-6" />
            </button>
            <div className="absolute top-4 left-1/2 -translate-x-1/2 font-mono text-xs text-zinc-500">
                {idx + 1} / {images.length}
            </div>

            <div className="relative flex items-center justify-center w-full h-full px-16 py-16">
                <AnimatePresence mode="wait">
                    <motion.img key={idx} src={resolveImg(images[idx])} alt=""
                        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                        transition={{ duration: 0.18 }}
                        className="max-w-full max-h-full object-contain select-none"
                        style={{ maxHeight: "calc(100vh - 140px)" }} draggable={false} />
                </AnimatePresence>

                {idx > 0 && (
                    <button onClick={() => setIdx(i => i - 1)}
                        className="absolute left-4 p-3 rounded-full bg-black/60 text-white hover:bg-black/80 border border-white/10 transition-all">
                        <ChevronLeft className="w-5 h-5" />
                    </button>
                )}
                {idx < images.length - 1 && (
                    <button onClick={() => setIdx(i => i + 1)}
                        className="absolute right-4 p-3 rounded-full bg-black/60 text-white hover:bg-black/80 border border-white/10 transition-all">
                        <ChevronRight className="w-5 h-5" />
                    </button>
                )}
            </div>

            {images.length > 1 && (
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 overflow-x-auto max-w-xl px-4">
                    {images.map((img, i) => (
                        <button key={i} onClick={() => setIdx(i)}
                            className={`shrink-0 w-14 h-10 overflow-hidden border-2 transition-all ${i === idx ? "border-nova-cyan" : "border-transparent opacity-40 hover:opacity-70"}`}>
                            <img src={resolveImg(img)} alt="" className="w-full h-full object-cover" />
                        </button>
                    ))}
                </div>
            )}
        </motion.div>
    );
}

export default function ReferenciaDetailPage() {
    const { id }   = useParams();
    const navigate = useNavigate();
    const [item, setItem]   = useState(null);
    const [loading, setLoading] = useState(true);
    const [lbIdx, setLbIdx] = useState(null);

    useEffect(() => {
        api.getReferencie()
            .then(items => {
                const found = items.find(i => i.id === id);
                if (!found) { navigate("/referencie", { replace: true }); return; }
                setItem(found);
            })
            .catch(() => navigate("/referencie", { replace: true }))
            .finally(() => setLoading(false));
    }, [id, navigate]);

    if (loading) return (
        <div className="min-h-screen bg-nova-black flex items-center justify-center">
            <div className="w-8 h-8 border-2 border-nova-cyan border-t-transparent rounded-full animate-spin" />
        </div>
    );
    if (!item) return null;

    const allImgs  = [item.image_url, ...(item.images || [])].filter(Boolean);
    const embedUrl = parseVideoEmbed(item.video_url);

    return (
        <div className="min-h-screen bg-nova-black text-white">
            <SEO
                title={item.title}
                description={item.description
                    ? item.description.replace(/<[^>]+>/g, "").slice(0, 160)
                    : `Realizácia: ${item.title}`}
                canonical={`/referencie/${item.id}`}
            />

            <Navigation />

            {/* Hero image */}
            {allImgs[0] && (
                <div className="relative w-full overflow-hidden pt-20" style={{ height: "clamp(280px, 52vh, 560px)" }}>
                    <img src={resolveImg(allImgs[0])} alt={item.title}
                        className="absolute inset-0 w-full h-full object-cover"
                        style={{ filter: "brightness(0.38)" }} />
                    <div className="absolute inset-0 bg-gradient-to-t from-nova-black via-nova-black/15 to-transparent" />
                    <div className="absolute inset-0 bg-gradient-to-r from-nova-black/50 to-transparent" />

                    <div className="relative h-full flex items-end pb-10 px-6 sm:px-12 max-w-5xl mx-auto">
                        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
                            <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-black tracking-tighter leading-tight text-white mb-4">
                                {item.title}
                            </h1>
                            <div className="flex flex-wrap gap-5">
                                {item.client && (
                                    <span className="flex items-center gap-1.5 text-zinc-300 text-sm">
                                        <User className="w-3.5 h-3.5 text-nova-cyan" /> {item.client}
                                    </span>
                                )}
                                {item.location && (
                                    <span className="flex items-center gap-1.5 text-zinc-300 text-sm">
                                        <MapPin className="w-3.5 h-3.5 text-nova-cyan" /> {item.location}
                                    </span>
                                )}
                                {item.event_date && (
                                    <span className="flex items-center gap-1.5 text-zinc-300 text-sm">
                                        <Calendar className="w-3.5 h-3.5 text-nova-cyan" /> {fmtDate(item.event_date)}
                                    </span>
                                )}
                            </div>
                        </motion.div>
                    </div>
                </div>
            )}

            {/* Body */}
            <div className="max-w-5xl mx-auto px-6 sm:px-12 py-12 space-y-12">

                {/* Description */}
                {item.description && (
                    <div className="prose prose-invert prose-sm max-w-none text-zinc-300 leading-relaxed
                        [&_h2]:font-heading [&_h2]:text-white [&_h2]:font-black [&_h2]:text-xl [&_h2]:mt-6 [&_h2]:mb-2
                        [&_h3]:font-heading [&_h3]:text-white [&_h3]:font-bold [&_h3]:text-base [&_h3]:mt-4 [&_h3]:mb-1.5
                        [&_a]:text-nova-cyan [&_a]:underline
                        [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_li]:mb-1"
                        dangerouslySetInnerHTML={{ __html: item.description }} />
                )}

                {/* Gallery */}
                {allImgs.length > 1 && (
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                        {allImgs.map((img, i) => (
                            <button key={i} onClick={() => setLbIdx(i)}
                                className={`overflow-hidden border border-white/6 hover:border-white/25 transition-all group ${i === 0 ? "col-span-2 sm:col-span-3" : ""}`}
                                style={{ aspectRatio: i === 0 ? "16/7" : "1/1" }}>
                                <img src={resolveImg(img)} alt=""
                                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                            </button>
                        ))}
                    </div>
                )}

                {/* Video */}
                {embedUrl && (
                    <div className="border border-white/8 aspect-video overflow-hidden">
                        <iframe src={embedUrl} title="Video" className="w-full h-full"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen />
                    </div>
                )}
            </div>

            <Footer />

            <AnimatePresence>
                {lbIdx !== null && (
                    <Lightbox key="lb" images={allImgs} startIdx={lbIdx} onClose={() => setLbIdx(null)} />
                )}
            </AnimatePresence>
        </div>
    );
}
